import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { Schedule } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { formatDate, formatEur } from '../lib/format';
import { Button, Card, Chip, Empty, Loading, PageHeader, StatCard, cap } from '../components/ui';

export function cadenceLabel(sch: Schedule, s: (x: string) => string): string {
  const base = s(cap(sch.cadence));
  return sch.cadence === 'monthly' ? `${base} · ${ordinal(sch.dayOfMonth)}` : `${base} · ${ordinal(sch.dayOfMonth)}`;
}
function ordinal(n: number) { return `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`; }

export function Recurring() {
  const { company } = useAuth();
  const { t, s, lang } = useI18n();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['recurring', company?.id], queryFn: () => capi<Schedule[]>('/recurring') });
  const items = q.data ?? [];
  const active = items.filter((i) => i.status === 'active');
  const next = active[0];
  const nextRunItems = next ? active.filter((i) => i.nextRunAt === next.nextRunAt) : [];
  const monthly = active.reduce((sum, i) => sum + (i.cadence === 'monthly' ? i.subtotalCents : i.cadence === 'quarterly' ? i.subtotalCents / 3 : i.subtotalCents / 12), 0);
  return (
    <>
      <PageHeader crumb={t('recCrumb')} title={t('recTitle')} sub={t('recSub')} actions={<Button variant="primary" icon="fas fa-plus" onClick={() => navigate('/recurring/new')}>{t('recAction')}</Button>} />
      {!q.data ? <Loading /> : (
        <div className="stack">
          <div className="grid kpis" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
            <StatCard label={t('activeSchedules')} value={String(active.length)} caption={t('acrossCompanies')} icon="fas fa-repeat" />
            <StatCard label={t('nextRun')} value={next ? formatDate(next.nextRunAt, lang).slice(0, 6) : '—'} caption={next ? t('invoicesAmount', { n: nextRunItems.length, amount: formatEur(nextRunItems.reduce((a, i) => a + i.totalCents, 0), { decimals: false }) }) : ''} icon="fas fa-calendar-day" tone="accent" />
            <StatCard label={t('monthlyRecurring')} value={formatEur(Math.round(monthly), { decimals: false })} caption={t('exclVat')} icon="fas fa-chart-line" tone="success" />
          </div>
          <Card pad={false} className="table">
            <table>
              <thead><tr><th>{t('colSchedule')}</th><th>{t('colCustomer')}</th><th>{t('colCadence')}</th><th>{t('colNextRun')}</th><th>{t('colStatus')}</th><th className="r">{t('colAmount')}</th></tr></thead>
              <tbody>
                {items.length === 0 && <Empty />}
                {items.map((sch) => (
                  <tr key={sch.id} className="click" onClick={() => navigate(`/recurring/${sch.id}`)}>
                    <td style={{ fontWeight: 600 }}>{sch.name}</td><td>{sch.customerName}</td>
                    <td className="secondary small">{cadenceLabel(sch, s)}</td>
                    <td className="mono small">{sch.status === 'paused' ? s('Paused') : formatDate(sch.nextRunAt, lang)}</td>
                    <td><Chip tone={sch.status === 'active' ? (sch.vatTreatment === 'reverse_charge' ? 'primary' : 'success') : sch.status === 'paused' ? 'warning' : 'neutral'}>{s(cap(sch.status))}{sch.vatTreatment === 'reverse_charge' ? ` · ${s('reverse charge')}` : ''}</Chip></td>
                    <td className="amount">{formatEur(sch.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </>
  );
}
